import { defaultColors } from 'ngx-avatars';
import { environment } from '../../environments/environment';
import moment from 'moment';
import { FileActivity } from '../core/settings/_models/document.model';

export function getSecuredRandom() {
    return crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296
};

export function getInitialsOfTheName(name: string) {
    if (!name) return '';
    return name
        .split(' ')
        .map(word => word[0].toUpperCase())
        .join('');
}

export function getAvatarColors(name: string) {
    const initials = getInitialsOfTheName(name).toLowerCase();
    const asciiCodeSum = calculateAsciiCode(initials);
    const avatarColors = defaultColors;
    const res = avatarColors[asciiCodeSum % avatarColors.length];
    return [res];
}

export function calculateAsciiCode(value: string): number {
    return value
        .split('')
        .map(letter => letter.charCodeAt(0))
        .reduce((previous, current) => previous + current, 0);
}

/**
 * Initiates a download of the provided CSV data as a file with the specified filename.
 *
 * @param csvData - The CSV data to be downloaded.
 * @param filename - The desired name of the downloaded file (without extension).
 * @param appendTimestamp - Optional flag to append the current timestamp to the filename.
 *                          Defaults to `false`. If `true`, the timestamp will be appended.
 * @remarks
 * This function creates a temporary anchor element, sets its `href` to a data URI containing the CSV data,
 * and triggers a click event to trigger the download of the file. The file will have a `.csv` extension.
 */
export function downloadCSVFile(csvData: string, filename: string, appendTimestamp = false): void {
    if (appendTimestamp) {
        filename += `-${getCurrentTimestamp()}`;
    }

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename + '.csv';

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// Return current timestamp in format: DD-MM-YYYY-hh-mm-AM/PM
function getCurrentTimestamp(): string {
    const date = new Date();
    const timestamp = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}-${(date.getHours() % 12)}-${date.getMinutes()}-${date.getHours() >= 12 ? 'PM' : 'AM'}`;
    return timestamp;
}

/**
 * Formats the given duration (in minutes) into years, days, hours, and minutes.
 *
 * @param {number} durationInMinutes - The duration in minutes.
 * @returns {string} A formatted string such as "1y 2d 5h 30m", or "-" if duration is not provided.
 */
export function formatDurationFromMinutes(durationInMinutes: number): string {
    if (!durationInMinutes) return '-';
    let hours = Math.floor(durationInMinutes / 60), minutes = durationInMinutes % 60, days = 0, years = 0;
    let result: string[] = [];
    if (hours >= 24) {
        days = Math.floor(hours / 24);
        hours = hours % 24;
    }
    if (days >= 365) {
        years = Math.floor(days / 365);
        days = days % 365;
    }
    if (years > 0) result.push(`${years}y`);
    if (days > 0) result.push(`${days}d`);
    if (hours > 0 || years > 0 || days > 0) result.push(`${hours}h`);
    result.push(`${minutes}m`);
    return result.join(' ');
}

export const urlRegex = /^((https?):\/\/)?(?:([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?(\/[^\s]*)?$/

export function isAzureBlobUrl(url) {
    const azureBlobRegex = /^https:\/\/[a-z0-9]+\.blob\.core\.windows\.net\/[^\/]+\/[^\/]+/;
    return azureBlobRegex.test(url);
}

function getAzureContainerFromUrl(url: string): string | null {
    const match = url.match(/^https:\/\/[a-z0-9]+\.blob\.core\.windows\.net\/([^\/]+)\//);
    return match ? match[1] : null;
}

function urlHasAzureSasToken(url: string): boolean {
    return /[?&](sig|sv)=/.test(url);
}

function urlHasCloudFrontToken(url: string): boolean {
    return /[?&](Policy|Signature|Key-Pair-Id)=/.test(url);
}

export function appendCloudAccessTokenToUrl(url: string) {
    if (!url) {
        return url;
    }
    url = url.replace(/#/g, '%23');
    if (isAzureBlobUrl(url)) {
        if (urlHasAzureSasToken(url)) {
            return url;
        }
        const container = getAzureContainerFromUrl(url);
        const sasKey = container === 'filesync' || container === 'uptimefilebrowser' ? 'azureSasFileBrowser' : 'azureSas';
        url = url + (localStorage.getItem(sasKey) ?? '');
    } else if (environment.runtime.CLOUD_SERVICE_PROVIDER === 'aws') {
        if (urlHasCloudFrontToken(url)) {
            return url;
        }
        url = url + (localStorage.getItem('awsAccessToken') ?? '');
    }
    return url
}

// Helps requests pass through firewall/WAF security gateways
function shortHash(value: string): string {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = (hash << 5) - hash + value.charCodeAt(i);
        hash |= 0; // force 32-bit integer
    }
    return Math.abs(hash).toString(36);
}

export function getBrandingLogoUrl(cacheBustValue?: string): string {
    if (!cacheBustValue) return '';
    return `${environment.API_URL}/api/public/branding/logo?v=${shortHash(cacheBustValue)}`;
}

export function getChatbotName(): string {
    if (!environment.runtime?.CUSTOM_SAAS_DEPLOYMENT) return 'Rooty';
    return environment.runtime?.CHATBOT_NAME || 'Rooty';
}

/** Path under which the on-premise file browser serves uploaded files (uptime_server, behind this app's gateway). */
const ON_PREM_FILE_PATH = '/api/file-browser/';

export function getFetchableUrl(rawUrl: string): string {
    if (!rawUrl) return '';
    try {
        let urlObj = new URL(rawUrl.replace(/#/g, '%23'));
        // On-premise uploads are stored with the server's own base URL, which can be an address only the containers
        // resolve (http://uptime_server:3000/…). The same path is served through this app's gateway: fetch it from here.
        if (urlObj.pathname.startsWith(ON_PREM_FILE_PATH) && urlObj.origin !== window.location.origin) {
            urlObj = new URL(urlObj.pathname + urlObj.search, window.location.origin);
        }
        urlObj.pathname = urlObj.pathname
            .split('/')
            .map(segment => segment ? encodeURIComponent(decodeURIComponent(segment)) : segment)
            .join('/');
        return appendCloudAccessTokenToUrl(urlObj.toString());
    } catch (e) {
        return appendCloudAccessTokenToUrl(rawUrl);
    }
}

export function secondsToHours(duration) {
    const hrs = Math.trunc(duration / 3600);
    const mins = Math.trunc((duration % 3600) / 60);
    let ret = "";

    if (hrs > 0) {
        ret += (hrs < 10 ? "0" : "") + hrs + ":" + (mins < 10 ? "0" : "");
    } else ret += "" + '00' + ":" + (mins < 10 ? "0" : "");
    ret += "" + mins
    return ret;
}

export const isMongoId = (v: any): boolean =>
    typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

export const allowedFileExtensions: string[] = ['txt', 'doc', 'docx', 'pdf', 'png', 'jpg', 'jpeg', 'dwg', 'ppt', 'pptx', 'csv', 'xlsx', 'xls', 'bmp'];

export const acceptedFileTypes: string = allowedFileExtensions.map(ext => `.${ext}`).join(',');

export function filterValidfiles(files: FileList): File[] {
    const validFiles = Array.from(files).filter(file => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ext && allowedFileExtensions.includes(ext);
    });
    return validFiles;
}

export function extractAndFormatFile(url: string): object {
    const fileName = url.substring(url.lastIndexOf('/') + 1);
    const lastDotIndex = fileName.lastIndexOf('.');
    const extension = fileName.substring(lastDotIndex + 1);
    const nameWithoutExt = fileName.substring(0, lastDotIndex);

    // Remove timestamp (the last underscore + digits)
    const finalName = nameWithoutExt.replace(/_\d{10,}$/g, '');

    return {
        name: `${finalName}.${extension}`,
        type: extension,
        url: url
    }
}

/**
 * Generates a new filename by appending a timestamp before the extension.
 * Used to avoid collisions when multiple uploads share the same base name.
 *
 * @param originalName - The original file name (e.g. "document.pdf").
 * @returns A new name with timestamp before the extension (e.g. "document_1709500000000.pdf").
 */
export function generateNewFileName(originalName: string): string {
    const timestamp = moment().valueOf();
    const lastPeriodIndex = originalName.lastIndexOf('.');
    const fileNameWithoutExtension = originalName.slice(0, lastPeriodIndex);
    const extension = originalName.slice(lastPeriodIndex + 1);
    return `${fileNameWithoutExtension}_${timestamp}.${extension}`;
}

/**
 * Parses a date string in local timezone.
 *
 * If the input is in "YYYY-MM-DD" format,
 * it appends "00:00" to treat it as the start of the day in local time.
 *
 * For other formats, it relies on the Date constructor,
 * which will interpret the string in local time if it doesn't contain timezone information.
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format
 *
 * @param dateTime - The date string to parse, expected in "YYYY-MM-DD" or "YYYY-MM-DD HH:mm" format.
 * @returns A Date object representing the parsed date and time in local timezone.
 */
export function parseDateInLocalTimeZone(dateTime: string): Date {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateTime)) {
        // force local timezone by adding time component when missing
        return new Date(dateTime + " 00:00");
    }
    return new Date(dateTime);
}

export function decodeFileName(name: string | undefined | null): string {
    if (!name) return '';
    try {
        return decodeURIComponent(name);
    } catch {
        return name;
    }
}

export function getDisplayFileName(fileActivity: FileActivity): string {
    return fileActivity.entityExtractions?.metadata?.title || decodeFileName(fileActivity.fileName) || 'Unknown File';
}

/** "name • N pages • Rev X" summary line shown under a document's title in the data-management documents table and viewer. */
export function getFileSubtext(fileActivity: FileActivity): string {
    const rawName = decodeFileName(fileActivity.fileName);
    const pages = fileActivity.metadata?.num_pages || 1;
    const rev = 'Rev ' + (fileActivity.metadata?.rev || '0');
    return `${rawName} • ${pages} pages • ${rev}`;
}

/**
 * Extracts creation timestamp in milliseconds from a MongoDB ObjectId string.
 * First 4 bytes (8 hex characters) of MongoDB _id encode the creation timestamp.
 */
export function getMongoIdTimestamp(mongoId: string): number {
    if (!mongoId || typeof mongoId !== 'string' || mongoId.length < 8) {
        return 0;
    }
    const timestampInSeconds = parseInt(mongoId.substring(0, 8), 16);
    return isNaN(timestampInSeconds) ? 0 : timestampInSeconds * 1000;
}